// @workflow assistant.reply-blocks
/**
 * Reply blocks (contract "Reply blocks (draft v1)"): a `block` event carries one block the UI draws
 * natively. The server builds every value from a tool result; this module checks a block against
 * the contract and keeps only what the components show. A block that fails a check is dropped
 * (the part keeps the reason, the component logs it and draws nothing) and the reply's text still
 * shows. Limits, kinds, tones and words are in blockContract.js; value types in blockValues.js.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { BLOCK_INVALID, BLOCK_KIND, BLOCK_KINDS, BLOCK_LIMITS, BLOCK_TEXT, CALLOUT_TONES, REF_KIND, TONE, VALUE_TYPE, VALUE_TYPES } from './blockContract.js';
import { cleanLabel, cleanTone, cleanValue, isFiniteNumber, isPlainObject } from './blockValues.js';
import { chipTarget, CHIP_KIND } from './chipActions.js';
import { UI_TOOL } from './uiToolContract.js';

/** The part type a block becomes (events.js PART.BLOCK). */
export const BLOCK_PART = 'block';

const SHORT_KEY = /^[A-Za-z][\w-]*$/;
const isAbsent = (value) => value === undefined || value === null;

/** A failed check: thrown inside one block's cleaning, caught once in cleanBlock. */
class Refused extends Error {
  constructor(reason) {
    super(reason);
    this.reason = reason;
  }
}
const refuse = (reason) => { throw new Refused(reason); };
const need = (value, reason) => (value === null || value === undefined ? refuse(reason) : value);
const optionalLabel = (value, max) => (isAbsent(value) ? null : need(cleanLabel(value, max), BLOCK_INVALID.LABEL));
const toneOf = (value, fallback = TONE.NEUTRAL) => need(cleanTone(value, fallback), BLOCK_INVALID.TONE);
const listOf = (value, min, max, reason) => (Array.isArray(value) && value.length >= min && value.length <= max ? value : refuse(reason));

/** A UI action this build runs (Buttons are actions), or null. Queries are never buttons. */
function runnable(action) {
  const target = isPlainObject(action) ? chipTarget({ action }) : null;
  return target && target.kind === CHIP_KIND.ACTION ? target.action : null;
}

// ---- shared: source, empty, refs -------------------------------------------------------------

function cleanSource(raw) {
  if (isAbsent(raw)) return null;
  const label = isPlainObject(raw) ? cleanLabel(raw.label) : null;
  if (!label) refuse(BLOCK_INVALID.SOURCE);
  if (!isAbsent(raw.asOf) && (typeof raw.asOf !== 'string' || Number.isNaN(Date.parse(raw.asOf)))) refuse(BLOCK_INVALID.SOURCE);
  const action = isAbsent(raw.action) ? null : runnable(raw.action) || refuse(BLOCK_INVALID.SOURCE);
  return {
    label,
    scope: cleanLabel(raw.scope) || null,
    asOf: raw.asOf || null,
    action,
    actionLabel: action ? cleanLabel(raw.action.label) || BLOCK_TEXT.OPEN : null,
  };
}

const cleanEmpty = (raw) => cleanLabel(raw, BLOCK_LIMITS.MAX_TEXT_LENGTH) || BLOCK_TEXT.EMPTY;

/**
 * The shared `source` and `empty` of a chart spec (visual). A bad source is left out and the chart
 * still draws: the chart's numbers come from the spec, the source line only says where.
 */
export function visualExtras(spec) {
  let source = null;
  try {
    source = cleanSource(spec && spec.source);
  } catch (err) {
    if (!(err instanceof Refused)) throw err;
  }
  // No words of its own: null, so the chart keeps its own ("No tasks to show" on a timeline).
  return { source, empty: cleanLabel(spec && spec.empty, BLOCK_LIMITS.MAX_TEXT_LENGTH) || null };
}

/** What every data block carries besides its data. */
const dataBlock = (raw, kind, fields) => ({ kind, ...fields, source: cleanSource(raw.source), empty: cleanEmpty(raw.empty) });

/**
 * The one map from a ref's kind to the UI action that opens it (Buttons are actions): the tool and
 * the argument that takes the ref's id. Every tool also takes the ref's facility. A tag and an
 * inspection (an AVA Inspection is a tag) open their card, a task opens on its board, a document
 * opens in the document view. A finding and a register row have no entry, so they show as plain
 * text: no UI tool opens one AI finding (its row is its card) or one row of a register.
 */
const REF_OPENS = Object.freeze({
  [REF_KIND.TAG]: Object.freeze({ name: UI_TOOL.TAGS_OPEN_PANEL, idArg: 'tagId' }),
  [REF_KIND.INSPECTION]: Object.freeze({ name: UI_TOOL.TAGS_OPEN_PANEL, idArg: 'tagId' }),
  [REF_KIND.TASK]: Object.freeze({ name: UI_TOOL.TASKS_OPEN_ON_BOARD, idArg: 'taskId' }),
  [REF_KIND.DOCUMENT]: Object.freeze({ name: UI_TOOL.DOCUMENTS_OPEN, idArg: 'documentId' }),
});

/**
 * The action a ref opens, from REF_OPENS, or null (a kind with no open action, or no id).
 * @returns {{ name: string, args: object } | null}
 */
export function refAction(ref) {
  const opens = ref && Object.prototype.hasOwnProperty.call(REF_OPENS, ref.kind) ? REF_OPENS[ref.kind] : null;
  if (!opens || typeof ref.id !== 'string' || !ref.id) return null;
  return { name: opens.name, args: { [opens.idArg]: ref.id, facilityId: ref.facilityId || null } };
}

// ---- number -----------------------------------------------------------------------------------

function cleanDelta(raw) {
  if (isAbsent(raw)) return null;
  if (!isPlainObject(raw) || !isFiniteNumber(raw.value)) refuse(BLOCK_INVALID.NUMBER);
  return { value: raw.value, period: optionalLabel(raw.period), tone: toneOf(raw.tone) };
}

function cleanTile(raw) {
  if (!isPlainObject(raw)) refuse(BLOCK_INVALID.TILES);
  const label = need(cleanLabel(raw.label), BLOCK_INVALID.LABEL);
  if (!isFiniteNumber(raw.value)) refuse(BLOCK_INVALID.NUMBER);
  const decimals = Number.isInteger(raw.decimals) && raw.decimals >= 0 ? raw.decimals : 0;
  return { label, value: raw.value, unit: optionalLabel(raw.unit), decimals, tone: toneOf(raw.tone), delta: cleanDelta(raw.delta) };
}

function cleanNumber(raw) {
  const tiles = Array.isArray(raw.tiles) && !raw.tiles.length ? [] : listOf(raw.tiles, BLOCK_LIMITS.MIN_TILES, BLOCK_LIMITS.MAX_TILES, BLOCK_INVALID.TILES).map(cleanTile);
  return dataBlock(raw, BLOCK_KIND.NUMBER, { tiles, line: optionalLabel(raw.line, BLOCK_LIMITS.MAX_TEXT_LENGTH) });
}

// ---- table ------------------------------------------------------------------------------------

function cleanColumn(raw) {
  const ok = isPlainObject(raw) && typeof raw.key === 'string' && raw.key.length <= BLOCK_LIMITS.MAX_KEY_LENGTH && SHORT_KEY.test(raw.key) && VALUE_TYPES.includes(raw.type);
  const label = ok ? cleanLabel(raw.label) : null;
  if (!label) refuse(BLOCK_INVALID.COLUMNS);
  const column = { key: raw.key, label, type: raw.type };
  if (raw.type !== VALUE_TYPE.NUMBER) return column;
  return { ...column, unit: cleanLabel(raw.unit) || null, decimals: Number.isInteger(raw.decimals) && raw.decimals >= 0 ? raw.decimals : 0, signed: raw.signed === true };
}

function cleanRow(raw, columns) {
  if (!isPlainObject(raw)) refuse(BLOCK_INVALID.ROWS);
  return Object.fromEntries(columns.map((column) => {
    const cell = cleanValue(column.type, raw[column.key]);
    return [column.key, cell.ok ? cell.value : refuse(BLOCK_INVALID.CELL)];
  }));
}

/** `{ label, action }`: a UI action this build runs (show all opens the app's own page). */
function cleanShowAll(raw) {
  if (isAbsent(raw)) return null;
  const label = isPlainObject(raw) ? cleanLabel(raw.label) : null;
  const action = label ? runnable(raw.action) : null;
  return action ? { label, action } : refuse(BLOCK_INVALID.LINK);
}

/** `{ url, fileName?, label? }`: a file inside the AVA API the server builds with all rows. Anything else: no button. */
function cleanExport(raw) {
  if (!isPlainObject(raw) || typeof raw.url !== 'string' || !raw.url) return null;
  return { label: cleanLabel(raw.label) || BLOCK_TEXT.EXPORT_CSV, url: raw.url, fileName: cleanLabel(raw.fileName) || null };
}

function cleanTable(raw) {
  const columns = listOf(raw.columns, 1, BLOCK_LIMITS.MAX_COLUMNS, BLOCK_INVALID.COLUMNS).map(cleanColumn);
  if (new Set(columns.map((c) => c.key)).size !== columns.length) refuse(BLOCK_INVALID.COLUMNS);
  if (!Array.isArray(raw.rows)) refuse(BLOCK_INVALID.ROWS);
  const rows = raw.rows.slice(0, BLOCK_LIMITS.TABLE_INLINE_ROWS).map((row) => cleanRow(row, columns));
  const total = isAbsent(raw.total) ? raw.rows.length : raw.total;
  if (!Number.isInteger(total) || total < raw.rows.length || (total > 0 && !rows.length)) refuse(BLOCK_INVALID.TOTAL);
  const rowAction = isAbsent(raw.rowAction) ? null : raw.rowAction;
  if (rowAction !== null && !columns.some((c) => c.key === rowAction && c.type === VALUE_TYPE.REF)) refuse(BLOCK_INVALID.ROW_ACTION);
  // Complete means every row is in the block; a page of 20 of 340 is not, whatever was said.
  const complete = raw.complete === true && total === rows.length;
  return dataBlock(raw, BLOCK_KIND.TABLE, {
    title: optionalLabel(raw.title),
    subtitle: optionalLabel(raw.subtitle),
    columns,
    rows,
    total,
    complete,
    sortable: complete && rows.length > 1,
    rowAction,
    showAll: cleanShowAll(raw.showAll),
    export: cleanExport(raw.export),
  });
}

// ---- record -----------------------------------------------------------------------------------

function cleanField(raw) {
  const label = isPlainObject(raw) && VALUE_TYPES.includes(raw.type) ? cleanLabel(raw.label) : null;
  const value = label ? cleanValue(raw.type, raw.value) : { ok: false };
  if (!value.ok) refuse(BLOCK_INVALID.FIELDS);
  const field = { label, type: raw.type, value: value.value };
  return raw.type === VALUE_TYPE.NUMBER ? { ...field, unit: cleanLabel(raw.unit) || null, decimals: Number.isInteger(raw.decimals) ? raw.decimals : 0 } : field;
}

/** Record actions follow the chip rules: `{ label, icon?, action }`, an action this build runs. */
function cleanRecordAction(raw) {
  const label = isPlainObject(raw) ? cleanLabel(raw.label) : null;
  const action = label ? runnable(raw.action) : null;
  return action ? { label, icon: cleanLabel(raw.icon) || null, action } : refuse(BLOCK_INVALID.ACTIONS);
}

function cleanRecord(raw) {
  const fields = listOf(raw.fields || [], 0, BLOCK_LIMITS.MAX_RECORD_FIELDS, BLOCK_INVALID.FIELDS).map(cleanField);
  const actions = listOf(raw.actions || [], 0, BLOCK_LIMITS.MAX_RECORD_ACTIONS, BLOCK_INVALID.ACTIONS).map(cleanRecordAction);
  const status = isAbsent(raw.status) ? null : cleanValue(VALUE_TYPE.STATUS, raw.status);
  if (status && !status.ok) refuse(BLOCK_INVALID.TONE);
  return dataBlock(raw, BLOCK_KIND.RECORD, {
    title: need(cleanLabel(raw.title), BLOCK_INVALID.LABEL),
    subtitle: optionalLabel(raw.subtitle),
    status: status ? status.value : null,
    fields,
    actions,
  });
}

// ---- breakdown, callout, suggestions ----------------------------------------------------------

function cleanPart(raw) {
  const label = isPlainObject(raw) ? cleanLabel(raw.label) : null;
  if (!label || !isFiniteNumber(raw.value) || raw.value < 0) refuse(BLOCK_INVALID.PARTS);
  return { label, value: raw.value, tone: toneOf(raw.tone), ask: cleanLabel(raw.ask, BLOCK_LIMITS.MAX_TEXT_LENGTH) || null };
}

function cleanBreakdown(raw) {
  const parts = Array.isArray(raw.parts) && !raw.parts.length ? []
    : listOf(raw.parts, BLOCK_LIMITS.MIN_PARTS, BLOCK_LIMITS.MAX_PARTS, BLOCK_INVALID.PARTS).map(cleanPart);
  const sum = parts.reduce((n, p) => n + p.value, 0);
  const total = isAbsent(raw.total) ? sum : raw.total;
  if (!isFiniteNumber(total) || total < sum) refuse(BLOCK_INVALID.TOTAL);
  // Largest first; a stable sort keeps the server's order between equal parts.
  const sorted = parts.map((p, i) => [p, i]).sort((a, b) => b[0].value - a[0].value || a[1] - b[1]).map(([p]) => p);
  return dataBlock(raw, BLOCK_KIND.BREAKDOWN, { title: optionalLabel(raw.title), total, parts: sorted });
}

/** `{ label, items, more? }`: a few names the callout's text is about; `more` counts the names left out. */
function cleanCalloutList(raw) {
  const label = isPlainObject(raw) ? cleanLabel(raw.label, BLOCK_LIMITS.MAX_LIST_LABEL_LENGTH) : null;
  if (!label) refuse(BLOCK_INVALID.LISTS);
  const items = listOf(raw.items, 1, BLOCK_LIMITS.MAX_LIST_ITEMS, BLOCK_INVALID.LISTS)
    .map((item) => need(cleanLabel(item, BLOCK_LIMITS.MAX_LIST_ITEM_LENGTH), BLOCK_INVALID.LISTS));
  const more = isAbsent(raw.more) ? 0 : raw.more;
  if (!Number.isInteger(more) || more < 0) refuse(BLOCK_INVALID.LISTS);
  return { label, items, more };
}

const cleanCalloutLists = (raw) => (isAbsent(raw) ? [] : listOf(raw, 1, BLOCK_LIMITS.MAX_CALLOUT_LISTS, BLOCK_INVALID.LISTS).map(cleanCalloutList));

function cleanCallout(raw, optional) {
  if (!CALLOUT_TONES.includes(raw.tone)) refuse(BLOCK_INVALID.TONE);
  const text = need(cleanLabel(raw.text, BLOCK_LIMITS.MAX_TEXT_LENGTH), BLOCK_INVALID.TEXT);
  // The text says what happened; the lists only name some of the tags, so bad lists never cost the text.
  return { kind: BLOCK_KIND.CALLOUT, tone: raw.tone, text, lists: optional(cleanCalloutLists, raw.lists, []) };
}

function cleanSuggestion(raw) {
  const label = isPlainObject(raw) ? cleanLabel(raw.label) : null;
  const ask = label ? cleanLabel(raw.ask, BLOCK_LIMITS.MAX_TEXT_LENGTH) : null;
  return ask ? { label, ask } : refuse(BLOCK_INVALID.SUGGESTIONS);
}

const cleanSuggestions = (raw) => ({
  kind: BLOCK_KIND.SUGGESTIONS,
  items: listOf(raw.items, 1, BLOCK_LIMITS.MAX_SUGGESTIONS, BLOCK_INVALID.SUGGESTIONS).map(cleanSuggestion),
});

const CLEANERS = Object.freeze({
  [BLOCK_KIND.NUMBER]: cleanNumber,
  [BLOCK_KIND.TABLE]: cleanTable,
  [BLOCK_KIND.RECORD]: cleanRecord,
  [BLOCK_KIND.BREAKDOWN]: cleanBreakdown,
  [BLOCK_KIND.CALLOUT]: cleanCallout,
  [BLOCK_KIND.SUGGESTIONS]: cleanSuggestions,
});

/**
 * Optional parts a block draws without (a callout's lists): a bad one is left out, the first
 * reason is kept, and the rest of the block still draws (OPEN_ITEMS.md: ui-left-out-parts).
 */
function leftOutParts() {
  let reason = null;
  const optional = (clean, value, empty) => {
    try {
      return clean(value);
    } catch (err) {
      if (!(err instanceof Refused)) throw err;
      reason = reason || err.reason;
      return empty;
    }
  };
  return { optional, reason: () => reason };
}

/**
 * @param {unknown} raw the event's `block`
 * @returns {{ ok: true, block: object, leftOut: string | null } | { ok: false, reason: string }}
 *   `leftOut`: why an optional part was left out of a block that still draws
 */
export function cleanBlock(raw) {
  if (!isPlainObject(raw)) return { ok: false, reason: BLOCK_INVALID.NOT_AN_OBJECT };
  if (!BLOCK_KINDS.includes(raw.kind)) return { ok: false, reason: BLOCK_INVALID.UNKNOWN_KIND };
  const parts = leftOutParts();
  try {
    const block = CLEANERS[raw.kind](raw, parts.optional);
    return { ok: true, block, leftOut: parts.reason() };
  } catch (err) {
    if (err instanceof Refused) return { ok: false, reason: err.reason };
    throw err;
  }
}

/** The data a block shows; with none, it shows its empty state. Callouts and suggestions are never empty. */
export function isEmptyBlock(block) {
  switch (block && block.kind) {
    case BLOCK_KIND.NUMBER: return !block.tiles.length;
    case BLOCK_KIND.TABLE: return !block.rows.length;
    case BLOCK_KIND.RECORD: return !block.fields.length && !block.actions.length;
    case BLOCK_KIND.BREAKDOWN: return !block.parts.length;
    default: return false;
  }
}

/**
 * The conversation part for a `block` event (events.js). A dropped block keeps its reason and
 * kind, so the component can log it; it never draws. A block that lost an optional part draws
 * and keeps that reason in `invalid` too, so it is logged the same way.
 */
export function blockPart(event) {
  const clean = cleanBlock(event && event.block);
  const base = { id: event.partId, type: BLOCK_PART };
  if (clean.ok) return { ...base, block: clean.block, invalid: clean.leftOut };
  return { ...base, block: null, invalid: clean.reason, kind: isPlainObject(event.block) ? String(event.block.kind) : null };
}
